/**
 * /forgot-password — Request a password reset link.
 *
 * Submits to POST /api/auth/forgot-password and always renders the same
 * success state regardless of whether the email exists (matches the
 * server's anti-enumeration response).
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MailCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Seo from '../components/Seo';
import Footer from '../components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email }, { withCredentials: false });
      setSent(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        // Pydantic email format error — surface it directly so the user can fix.
        toast.error(detail[0]?.msg || 'Please enter a valid email.');
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="forgot-password-page">
      <Seo
        title="Reset your Forge Ledger password"
        description="Forgot your Forge Ledger password? Enter your email and we'll send a secure one-time reset link."
        path="/forgot-password"
      />
      <div className="flex-1 flex">
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/login" className="inline-flex items-center gap-2 text-sm text-[#64748B] hover:text-[#0052FF] mb-6" data-testid="forgot-password-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to sign in
          </Link>

          {sent ? (
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-8 shadow-sm" data-testid="forgot-password-success">
              <div className="w-12 h-12 rounded-xl bg-[#ECFDF5] flex items-center justify-center mb-4">
                <MailCheck className="w-6 h-6 text-[#047857]" />
              </div>
              <h1 className="text-2xl font-extrabold text-[#020617] mb-2">Check your inbox</h1>
              <p className="text-[#64748B] mb-1">
                If <strong className="text-[#020617]">{email}</strong> is registered with Forge Ledger,
                a reset link is on its way.
              </p>
              <p className="text-sm text-[#94A3B8]">
                The link expires in 60 minutes and can only be used once. Didn&apos;t get anything?
                Check your spam folder, or {' '}
                <button
                  type="button"
                  onClick={() => { setSent(false); }}
                  className="text-[#0052FF] hover:underline"
                  data-testid="forgot-password-try-again"
                >
                  try a different email
                </button>.
              </p>
            </div>
          ) : (
            <>
              <h1 className="text-4xl tracking-tighter font-extrabold text-[#020617] mb-2" data-testid="forgot-password-title">
                Forgot your password?
              </h1>
              <p className="text-base text-[#64748B] mb-8">
                Enter the email you signed up with — we&apos;ll send you a link to choose a new one.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="forgot-password-form">
                <div>
                  <Label htmlFor="email" className="text-sm font-medium text-[#020617]">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-1.5 bg-white border border-[#E2E8F0] rounded-lg px-4 py-2 text-[#020617] focus:border-[#0052FF] focus:ring-1 focus:ring-[#0052FF]"
                    placeholder="you@example.com"
                    data-testid="forgot-password-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#0052FF] text-white rounded-lg px-6 py-2.5 font-medium hover:bg-[#003ECC] transition-colors"
                  data-testid="forgot-password-submit-button"
                >
                  {submitting ? 'Sending…' : 'Send reset link'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>

      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-[#0052FF] to-[#003ECC] items-center justify-center p-12">
        <div className="max-w-md text-white">
          <h2 className="text-3xl font-bold mb-4">Locked out happens to the best of us.</h2>
          <p className="text-lg opacity-90">
            We use a single-use, one-hour reset link — no security questions, no SMS, just an email.
          </p>
        </div>
      </div>
      </div>
      <Footer variant="full" />
    </div>
  );
};

export default ForgotPassword;
